import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { mergeCustomerProfiles, type CustomerProfile } from '@/lib/customerProfiles';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export type Customer = CustomerProfile;

export type CustomerEnquiry = {
  id: string;
  customer_id: string | null;
  client_name: string | null;
  client_email: string | null;
  client_phone: string | null;
  address: string | null;
  subject: string | null;
  status: string;
  created_at: string;
};

export type CustomerBooking = {
  id: string;
  customer_id: string | null;
  client_name: string | null;
  client_email: string | null;
  client_phone: string | null;
  address: string | null;
  title: string;
  scheduled_date: string;
  start_time: string;
  status: string;
};

export type MessageThread = {
  id: string;
  customer_id: string;
  enquiry_id: string | null;
  booking_id: string | null;
  subject: string | null;
  created_at: string;
};

export type CustomerMessage = {
  id: string;
  thread_id: string;
  sender_type: 'customer' | 'admin' | 'system';
  body: string;
  created_at: string;
};

export interface CustomersData {
  customers: Customer[];
  enquiries: CustomerEnquiry[];
  bookings: CustomerBooking[];
  threads: MessageThread[];
  messages: CustomerMessage[];
}

/** Loads customer profiles from real customer rows plus legacy booking/enquiry contact snapshots. */
export function useCustomersData(orgId: string | null | undefined) {
  return useQuery({
    queryKey: ['customers-admin', orgId],
    enabled: Boolean(orgId),
    queryFn: async (): Promise<CustomersData> => {
      const [customersRes, enquiriesRes, bookingsRes, threadsRes] = await Promise.all([
        db
          .from('customers')
          .select('id, name, email, phone, address, created_at')
          .eq('organization_id', orgId)
          .order('created_at', { ascending: false }),
        db
          .from('enquiries')
          .select('id, customer_id, client_name, client_email, client_phone, address, subject, status, created_at')
          .eq('organization_id', orgId)
          .order('created_at', { ascending: false }),
        db
          .from('jobs')
          .select('id, customer_id, client_name, client_email, client_phone, address, title, scheduled_date, start_time, status')
          .eq('organization_id', orgId)
          .order('scheduled_date', { ascending: false }),
        db
          .from('customer_message_threads')
          .select('id, customer_id, enquiry_id, booking_id, subject, created_at')
          .eq('organization_id', orgId)
          .order('created_at', { ascending: false }),
      ]);

      if (customersRes.error) throw customersRes.error;
      if (enquiriesRes.error) throw enquiriesRes.error;
      if (bookingsRes.error) throw bookingsRes.error;
      if (threadsRes.error) throw threadsRes.error;

      const enquiries = (enquiriesRes.data || []) as CustomerEnquiry[];
      const bookings = (bookingsRes.data || []) as CustomerBooking[];
      const customers = mergeCustomerProfiles([
        ...((customersRes.data || []) as Array<{ id: string; name: string | null; email: string | null; phone: string | null; address: string | null; created_at: string | null }>).map((customer) => ({
          customer_id: customer.id,
          name: customer.name,
          email: customer.email,
          phone: customer.phone,
          address: customer.address,
          created_at: customer.created_at,
        })),
        ...enquiries.map((enquiry) => ({
          customer_id: enquiry.customer_id,
          name: enquiry.client_name,
          email: enquiry.client_email,
          phone: enquiry.client_phone,
          address: enquiry.address,
          created_at: enquiry.created_at,
        })),
        ...bookings.map((booking) => ({
          customer_id: booking.customer_id,
          name: booking.client_name,
          email: booking.client_email,
          phone: booking.client_phone,
          address: booking.address,
          created_at: booking.scheduled_date,
        })),
      ]);
      const threads = (threadsRes.data || []) as MessageThread[];

      let messages: CustomerMessage[] = [];
      if (threads.length > 0) {
        const { data: messageRows, error: messageError } = await db
          .from('customer_messages')
          .select('id, thread_id, sender_type, body, created_at')
          .eq('organization_id', orgId)
          .in('thread_id', threads.map((t) => t.id))
          .order('created_at', { ascending: true });
        if (messageError) throw messageError;
        messages = (messageRows || []) as CustomerMessage[];
      }

      return { customers, enquiries, bookings, threads, messages };
    },
  });
}

export interface SendReplyInput {
  threadId: string;
  orgId: string;
  customerId: string;
  senderId: string | undefined;
  body: string;
}

/** Sends an admin reply to a customer message thread and refreshes the customers data. */
export function useSendCustomerReply() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: SendReplyInput): Promise<void> => {
      const { error } = await db.from('customer_messages').insert({
        thread_id: input.threadId,
        organization_id: input.orgId,
        customer_id: input.customerId,
        sender_type: 'admin',
        sender_user_id: input.senderId,
        body: input.body,
      });
      if (error) throw error;
    },
    onSuccess: (_result, input) => {
      queryClient.invalidateQueries({ queryKey: ['customers-admin', input.orgId] });
    },
  });
}
